import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos, Field, GlassCard, GoldButton, SectionTitle } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';

// ============================================================================
// AKESO — CHANGE worksheets, ported 1:1 from web src/app/akeso/page.tsx.
// Backed by the same `akeso_tables` table; field keys match DB column names
// exactly so patches can be sent straight through without snake/camel mapping.
// ============================================================================

const AKESO_TABLE = 'akeso_tables';

interface AkesoTable {
  id: string;
  userId: string;
  title: string;
  cue: string;
  habit: string;
  abate: string;
  negative: string;
  goal: string;
  ensure: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

type AkesoField = 'title' | 'cue' | 'habit' | 'abate' | 'negative' | 'goal' | 'ensure';

// The 6 CHANGE rows, in order. Keys correspond 1:1 to DB columns.
const ROWS: Array<{ key: Exclude<AkesoField, 'title'>; letter: string; label: string }> = [
  { key: 'cue', letter: 'C', label: 'Cue:' },
  { key: 'habit', letter: 'H', label: 'Habit:' },
  { key: 'abate', letter: 'A', label: 'ABATE' },
  { key: 'negative', letter: 'N', label: 'Negative' },
  { key: 'goal', letter: 'G', label: 'Goal/' },
  { key: 'ensure', letter: 'E', label: 'Ensure/' },
];

function mapRow(row: any): AkesoTable {
  return {
    id: row.id,
    userId: row.user_id,
    title: row.title ?? '',
    cue: row.cue ?? '',
    habit: row.habit ?? '',
    abate: row.abate ?? '',
    negative: row.negative ?? '',
    goal: row.goal ?? '',
    ensure: row.ensure ?? '',
    sortOrder: row.sort_order ?? 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function AkesoTool(_props: ToolProps) {
  const { t: pal } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [tables, setTables] = useState<AkesoTable[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-table debounce bookkeeping — each AKESO table has its own timer and a
  // pending-patch bucket so fast typing in one row doesn't fire six updates.
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingUpdatesRef = useRef<Record<string, Partial<Record<AkesoField, string>>>>({});

  const load = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    const { data, error: err } = await supabase
      .from(AKESO_TABLE)
      .select('*')
      .eq('user_id', userId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (err) {
      console.error('Error loading AKESO tables:', err);
      setError('Could not load AKESO tables.');
    } else {
      setError(null);
      setTables((data || []).map(mapRow));
    }
    setIsLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Flush any pending debounced writes on unmount so nothing is lost on nav-away.
  useEffect(() => {
    return () => {
      for (const id of Object.keys(saveTimersRef.current)) {
        clearTimeout(saveTimersRef.current[id]);
      }
      const pending = pendingUpdatesRef.current;
      for (const id of Object.keys(pending)) {
        const patch = pending[id];
        if (!patch) continue;
        supabase
          .from(AKESO_TABLE)
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq('id', id)
          .then(() => {}, () => {});
      }
      pendingUpdatesRef.current = {};
      saveTimersRef.current = {};
    };
  }, []);

  const scheduleSave = useCallback((tableId: string, patch: Partial<Record<AkesoField, string>>) => {
    pendingUpdatesRef.current[tableId] = { ...(pendingUpdatesRef.current[tableId] || {}), ...patch };
    if (saveTimersRef.current[tableId]) clearTimeout(saveTimersRef.current[tableId]);
    saveTimersRef.current[tableId] = setTimeout(async () => {
      const updates = pendingUpdatesRef.current[tableId];
      delete pendingUpdatesRef.current[tableId];
      delete saveTimersRef.current[tableId];
      if (!updates) return;
      try {
        const { error: err } = await supabase
          .from(AKESO_TABLE)
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', tableId);
        if (err) throw err;
      } catch (err) {
        console.error('Error saving AKESO table:', err);
        setError('Could not save changes.');
      }
    }, 500);
  }, []);

  const handleFieldChange = (tableId: string, field: AkesoField, value: string) => {
    setTables((prev) => prev.map((t) => (t.id === tableId ? { ...t, [field]: value } : t)));
    scheduleSave(tableId, { [field]: value });
  };

  const handleAddTable = async () => {
    if (!userId) return;
    const maxSort = tables.reduce((m, t) => Math.max(m, t.sortOrder), -1);
    const now = new Date().toISOString();
    try {
      const { data, error: err } = await supabase
        .from(AKESO_TABLE)
        .insert({
          user_id: userId,
          title: '',
          cue: '',
          habit: '',
          abate: '',
          negative: '',
          goal: '',
          ensure: '',
          sort_order: maxSort + 1,
          created_at: now,
          updated_at: now,
        })
        .select('*')
        .single();
      if (err) throw err;
      if (data) {
        const newTable = mapRow(data);
        setTables((prev) => (prev.some((t) => t.id === newTable.id) ? prev : [...prev, newTable]));
      }
    } catch (err) {
      console.error('Error creating AKESO table:', err);
      setError('Could not create table.');
    }
  };

  const deleteTable = async (table: AkesoTable) => {
    const id = table.id;
    // Clear any pending writes for this table so the debounce doesn't resurrect it.
    if (saveTimersRef.current[id]) {
      clearTimeout(saveTimersRef.current[id]);
      delete saveTimersRef.current[id];
    }
    delete pendingUpdatesRef.current[id];
    // Optimistic removal.
    setTables((prev) => prev.filter((t) => t.id !== id));
    try {
      const { error: err } = await supabase.from(AKESO_TABLE).delete().eq('id', id);
      if (err) throw err;
    } catch (err) {
      console.error('Error deleting AKESO table:', err);
      setError('Could not delete table.');
      await load();
    }
  };

  const confirmDelete = (table: AkesoTable) => {
    Alert.alert(
      'Delete this table?',
      table.title ? `"${table.title}" will be permanently deleted.` : 'This CHANGE worksheet will be permanently deleted.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => void deleteTable(table) },
      ],
    );
  };

  if (isLoading) {
    return (
      <Atmos style={styles.center}>
        <ActivityIndicator color={pal.gold} />
      </Atmos>
    );
  }

  return (
    <Atmos>
    <ScrollView style={styles.root} contentContainerStyle={styles.content}>
      <SectionTitle
        right={
          <GoldButton
            label="+ Add Table"
            onPress={handleAddTable}
            style={[styles.addButton, !userId && styles.addButtonDisabled]}
          />
        }
        style={styles.headerRow}
      >
        AKESO
      </SectionTitle>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {tables.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text style={styles.empty}>No tables yet.</Text>
          <Text style={styles.emptyHint}>Tap “+ Add Table” to create your first CHANGE worksheet.</Text>
        </View>
      ) : (
        <View style={styles.tablesList}>
          {tables.map((table) => (
            <GlassCard key={table.id} style={styles.tableCard}>
              <View style={styles.titleRow}>
                <Field
                  style={styles.titleField}
                  inputStyle={styles.titleInput}
                  value={table.title}
                  onChangeText={(t) => handleFieldChange(table.id, 'title', t)}
                  placeholder="Untitled"
                />
                <Pressable
                  style={styles.deleteButton}
                  onPress={() => confirmDelete(table)}
                  hitSlop={8}
                  accessibilityLabel="Delete table"
                >
                  <Text style={styles.deleteText}>✕</Text>
                </Pressable>
              </View>

              <View style={styles.grid}>
                {ROWS.map((row, idx) => (
                  <View
                    key={row.key}
                    style={[styles.gridRow, idx < ROWS.length - 1 && styles.gridRowDivider]}
                  >
                    <View style={styles.letterCell}>
                      <Text style={styles.letterText}>{row.letter}</Text>
                    </View>
                    <View style={styles.labelCell}>
                      <Text style={styles.labelText}>{row.label}</Text>
                    </View>
                    <View style={styles.valueCell}>
                      <Field
                        style={styles.valueField}
                        inputStyle={styles.valueInput}
                        value={table[row.key]}
                        onChangeText={(t) => handleFieldChange(table.id, row.key, t)}
                        placeholder=""
                        multiline
                        textAlignVertical="top"
                      />
                    </View>
                  </View>
                ))}
              </View>
            </GlassCard>
          ))}
        </View>
      )}
    </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    content: { padding: 16, paddingBottom: 32 },
    error: { color: t.lapsed, marginBottom: 8, fontSize: 13 },

    headerRow: { marginBottom: 14, gap: 10 },
    addButton: { height: 36, paddingHorizontal: 14 },
    addButtonDisabled: { opacity: 0.5 },

    emptyBox: { alignItems: 'center', paddingVertical: 32 },
    empty: { color: t.ivorySoft, fontStyle: 'italic', textAlign: 'center', fontSize: 13 },
    emptyHint: { color: t.ivoryFaint, fontSize: 12, marginTop: 4, textAlign: 'center' },

    tablesList: { gap: 16 },
    tableCard: { padding: 12, gap: 10 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    titleField: { flex: 1 },
    titleInput: { fontSize: 16, fontWeight: '700', fontFamily: DISPLAY_FONT, letterSpacing: 1 },
    deleteButton: {
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    deleteText: { color: t.lapsed, fontSize: 14, fontWeight: '700' },

    grid: {
      borderRadius: 14,
      overflow: 'hidden',
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.lineFaint,
    },
    gridRow: { flexDirection: 'row', alignItems: 'stretch' },
    gridRowDivider: { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: t.navyLine },
    letterCell: {
      width: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRightWidth: StyleSheet.hairlineWidth,
      borderRightColor: t.navyLine,
      paddingVertical: 8,
    },
    letterText: { fontSize: 13, fontWeight: '800', color: t.gold },
    labelCell: {
      width: 74,
      justifyContent: 'center',
      borderRightWidth: StyleSheet.hairlineWidth,
      borderRightColor: t.navyLine,
      paddingVertical: 8,
      paddingHorizontal: 6,
    },
    labelText: { fontSize: 11, fontWeight: '600', color: t.ivorySoft },
    valueCell: { flex: 1, minHeight: 40, justifyContent: 'center', padding: 4 },
    valueField: { flex: 1 },
    valueInput: { fontSize: 13, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10 },
  }),
);
